import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../axiosInstance';
import AuthContext from '../AuthContext';
import { LanguageContext } from '../LanguageContext';

const copy = {
  en: {
    title: 'Message',
    loading: 'Loading message…',
    from: 'From',
    to: 'To',
    sentOn: 'Sent',
    received: 'Received',
    caseReference: 'Booking reference',
    back: 'Back to inbox',
    reply: 'Reply',
    delete: 'Delete',
    print: 'Print',
    urgent: 'Urgent',
    loadError: 'We could not load this message. Please try again.',
    you: 'You',
    programTeam: 'Program team'
  },
  fr: {
    title: 'Message',
    loading: 'Chargement du message…',
    from: 'De',
    to: 'À',
    sentOn: 'Envoyé le',
    received: 'Reçu le',
    back: 'Retour à la boîte de réception',
    reply: 'Répondre',
    delete: 'Supprimer',
    print: 'Imprimer',
    urgent: 'Urgent',
    loadError: 'Impossible de charger ce message. Veuillez réessayer.',
    you: 'Vous',
    programTeam: 'Équipe du programme'
  }
};

const MessageDetails = () => {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { language } = useContext(LanguageContext);
  const t = useMemo(() => copy[language] || copy.en, [language]);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMessage = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get(`/api/messages/${messageId}`);
        setMessage(data);
      } catch (err) {
        console.error('Failed to load message details', err);
        setError(t.loadError);
      } finally {
        setLoading(false);
      }
    };
    if (user) loadMessage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, user]);

  const handleReply = () => {
    navigate(`/messages/new?replyTo=${messageId}`);
  };

  const handleDelete = async () => {
    try {
  await api.delete(`/api/messages/${messageId}`);
      navigate('/messages');
    } catch (err) {
      console.error('Failed to delete message', err);
      setError(t.loadError);
    }
  };

  const handleBack = () => {
    navigate('/messages');
  };

  const renderBody = () => {
    if (!message?.body) return null;
    const normalized = String(message.body).replace(/\r\n/g, '\n');
    const blocks = normalized
      .split(/\n{2,}/)
      .map(block => block.replace(/\s+$/g, ''))
      .filter(block => block.trim().length > 0);
    return blocks.map((block, blockIndex) => {
      const lines = block.split('\n');
      return (
        <p key={blockIndex} className="govuk-body">
          {lines.map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {line}
              {lineIndex < lines.length - 1 ? <br /> : null}
            </React.Fragment>
          ))}
        </p>
      );
    });
  };

  const renderForms = () => {
    const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
    if (!attachments.length) {
      return null;
    }
    const resolveAttachmentRank = (attachment) => {
      const typeKey = String(attachment?.checklist_doc_type || '').trim().toLowerCase();
      const nameKey = String(attachment?.workflow_name || '').trim().toLowerCase();
      if (typeKey === 'funding_agreement') return 10;
      if (typeKey === 'client_acknowledgement') return 20;
      if (typeKey === 'eft_form' || typeKey === 'eft_or_wire_transfer_form' || typeKey === 'eft') return 30;
      if (typeKey === 'assessment_approval_letter') return 40;
      if (nameKey.includes('funding agreement')) return 10;
      if (nameKey.includes('acknowledgement')) return 20;
      if (nameKey.includes('eft') || nameKey.includes('wire transfer')) return 30;
      if (nameKey.includes('letter of approval')) return 40;
      return 100;
    };
    const sortedAttachments = [...attachments].sort((a, b) => {
      const rankDiff = resolveAttachmentRank(a) - resolveAttachmentRank(b);
      if (rankDiff !== 0) return rankDiff;
      return Number(a?.signing_request_id || 0) - Number(b?.signing_request_id || 0);
    });
    const resolveFormLabel = (attachment) => {
      const workflowName = typeof attachment?.workflow_name === 'string' ? attachment.workflow_name.trim() : '';
      return workflowName || `Form ${attachment.signing_request_id}`;
    };
    return (
      <div className="govuk-!-margin-top-4">
        <h2 className="govuk-heading-m">Forms attached</h2>
        <ul className="govuk-list govuk-list--bullet">
          {sortedAttachments.map(att => (
            <li key={att.signing_request_id}>
              <span className="govuk-!-font-weight-bold">{resolveFormLabel(att)}</span>{' '}
              <Link className="govuk-link" to={`/documents/${att.signing_request_id}`}>Open</Link>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="govuk-width-container govuk-!-margin-top-3">
        <main className="govuk-main-wrapper" role="main">
          <p className="govuk-body">{t.loading}</p>
        </main>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="govuk-width-container govuk-!-margin-top-3">
        <main className="govuk-main-wrapper" role="main">
          <p className="govuk-body">{t.loadError}</p>
          <button type="button" className="govuk-button govuk-button--secondary" onClick={handleBack}>{t.back}</button>
        </main>
      </div>
    );
  }

  const fromLabel = message.direction === 'received' ? t.programTeam : t.you;
  const toLabel = message.direction === 'sent' ? t.programTeam : t.you;
  const dateLabel = message.direction === 'sent' ? t.sentOn : t.received;
  const bookingReference = message.tracking_id || null;

  return (
    <div className="govuk-width-container govuk-!-margin-top-3">
      <main className="govuk-main-wrapper" role="main">
        {error && (
          <div className="govuk-error-summary" role="alert" aria-live="assertive">
            <h2 className="govuk-error-summary__title">{error}</h2>
          </div>
        )}
        <h1 className="govuk-heading-l">{message.subject || t.title}</h1>
        <dl className="govuk-summary-list govuk-!-margin-bottom-5">
          <div className="govuk-summary-list__row">
            <dt className="govuk-summary-list__key">{t.from}</dt>
            <dd className="govuk-summary-list__value">{fromLabel}</dd>
          </div>
          <div className="govuk-summary-list__row">
            <dt className="govuk-summary-list__key">{t.to}</dt>
            <dd className="govuk-summary-list__value">{toLabel}</dd>
          </div>
          {bookingReference ? (
            <div className="govuk-summary-list__row">
              <dt className="govuk-summary-list__key">{t.caseReference}</dt>
              <dd className="govuk-summary-list__value">{bookingReference}</dd>
            </div>
          ) : null}
          <div className="govuk-summary-list__row">
            <dt className="govuk-summary-list__key">{dateLabel}</dt>
            <dd className="govuk-summary-list__value">{new Date(message.created_at).toLocaleString()}</dd>
          </div>
        </dl>
        <div className="govuk-body">
          {message.urgent ? (<strong className="govuk-tag govuk-tag--red govuk-!-margin-bottom-2">{t.urgent}</strong>) : null}
          {renderBody()}
        </div>
        {renderForms()}
        <div className="govuk-button-group govuk-!-margin-top-4">
          <button type="button" className="govuk-button" onClick={handleReply}>{t.reply}</button>
          <button type="button" className="govuk-button govuk-button--warning" onClick={handleDelete}>{t.delete}</button>
          <button type="button" className="govuk-button govuk-button--secondary" onClick={() => window.print()}>{t.print}</button>
          <button type="button" className="govuk-button govuk-button--secondary" onClick={handleBack}>{t.back}</button>
        </div>
      </main>
    </div>
  );
};

export default MessageDetails;
